import {Injectable} from '@angular/core';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {of, zip} from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import {AccessPoint} from '../../models/access-point/access-point';
import {EventConfigurationMetaData} from '../../models/events/event-interface';
import {User} from '../../models/user/user.interface';
import {AccessPointService} from '../../services/access-point.service';
import {EventsService} from '../../services/events.service';
import {UserService} from '../../services/user.service';
import {
  GetEventsConfiguration,
  LoadMoreEventsResults,
  SelectEvent,
  SetEventsFilter,
  UpdateRealTimeEvents,
  UpdateSelectedEvent,
  SetIsPrinting
} from './events.actions';
import {EventsStateModel} from './events.model';
import {take} from 'rxjs/operators';
import {EnvService} from '../../../models/env.service';
import {MonitoringService} from '../../../services/logging.service';
import UuidHelper from 'src/app/services/uuid.helper';

@State<EventsStateModel>({
  name: 'events',
  defaults: {
    events: null,
    eventsFiltered: null,
    realTimeEvents: [],
    totalEventsCount: 0,
    selectedEvent: null,
    currentPage: 0,
    isTouched: false,
    isLoadingData: false,
    eventsFilter: {
      search: null,
      filter: {
        type: 0,
        timeFrame: 1
      }
    },
    configuration: null,
    isPrinting: false
  }
})

@Injectable()
export class EventsState {
  constructor(
    private eventsService: EventsService,
    private userService: UserService,
    private accessPointService: AccessPointService,
    private store: Store,
    private commonService: CommonService,
    private env: EnvService,
    private monitoringService: MonitoringService,) {
  }

  @Action(SetEventsFilter)
  async setEventsFilter(ctx: StateContext<EventsStateModel>, action: SetEventsFilter) {
    ctx.patchState({eventsFilter: {...action.filter}});
  }

  @Action(LoadMoreEventsResults)
  async loadMoreResults(ctx: StateContext<EventsStateModel>, action: LoadMoreEventsResults) {
    ctx.patchState({
      isLoadingData: true
    });

    let eventsFilter = ctx.getState().eventsFilter;
    let {currentPage, events} = ctx.getState();

    if(eventsFilter.isSearchInputEvent && eventsFilter.search) {
      eventsFilter.search = eventsFilter.search.replace( /\\{2,}/gm,'\\');
      const eventsFiltered = events.filter((e) => {
        return (
          this.commonService.nonCaseSensitiveIncludes(e.generator, eventsFilter.search) ||
          this.commonService.nonCaseSensitiveIncludes(e.timeFrom, eventsFilter.search) ||
          this.commonService.nonCaseSensitiveIncludes(e.name, eventsFilter.search) ||
          this.commonService.nonCaseSensitiveIncludes(e?.alert?.description, eventsFilter.search) ||
          this.commonService.nonCaseSensitiveIncludes(e.location, eventsFilter.search)
        );
      });

      return ctx.patchState({
        currentPage: 0,
        eventsFilter: {
          search: eventsFilter.search,
          filter: eventsFilter.filter
        },
        eventsFiltered,
        isLoadingData: false
      });
    }

    if (action.resetChunk) {
      ctx.patchState({currentPage: 0});
    }

    let nextPage = currentPage;
    const chunkSize: number = this.env.environment.events.requestedAmount;
    ctx.patchState({isLoadingData: true});
    let eventResults = await this.eventsService.getEvents(++nextPage, chunkSize, eventsFilter);

    if(!eventResults){return false;}

    eventResults.results.forEach(event => {
      if(!event.id || event.id === '0'){
        event.id = UuidHelper.create_UUID();
      }
    });

    if (eventsFilter.filter.type === 4) {//System events
      let eventsWithZeroId = eventResults.results.filter(event => event.id === '0');
      if (eventsWithZeroId && eventsWithZeroId.length === eventResults.results.length) {
        eventResults.results = eventResults.results.map((event, index) => {
          event.id = `System_${index}`;
          return event;
        });
      }
    }
    ctx.patchState({isLoadingData: false});
    const eventList = action.resetChunk ? [] : events;
    if (eventResults.results) {
      eventResults.results.sort(this.commonService.compareByNameAscFn);
    }

    ctx.patchState({
      isTouched: true,
      events: [...eventList, ...eventResults.results],
      eventsFiltered: [...eventList, ...eventResults.results],
      totalEventsCount: eventResults.totalCount
    });
    if (eventList.length !== ctx.getState().events.length) {
      ctx.patchState({
        currentPage: nextPage
      });
    }
  }

  @Action(GetEventsConfiguration)
  async getEventsConfiguration(ctx: StateContext<EventsStateModel>) {
    const configuration: EventConfigurationMetaData = await this.eventsService.getConfiguration();
    if (configuration) {
      let allType = configuration.eventTypes.find(type => type.id === 0);
      if (allType) {
        allType.description = 'All Event Types';
      }
      ctx.patchState({configuration});
    }
  }

  @Action(UpdateRealTimeEvents)
  updateRealTimeEvents(ctx: StateContext<EventsStateModel>, action: UpdateRealTimeEvents) {
    let newEvents = [...action.events];
    ctx.patchState({realTimeEvents:  newEvents});
  }

  @Action(UpdateSelectedEvent)
  async updateSelectedEvent(ctx: StateContext<EventsStateModel>, action: UpdateSelectedEvent) {

    ctx.patchState({selectedEvent:  action.event});
  }
  @Action(SelectEvent)
  async selectEvent(ctx: StateContext<EventsStateModel>, action: SelectEvent) {
    if (action.event && action.event.eventTypeId !== null) {
      ctx.patchState({isLoadingData: true});
      zip(action.event.event?.eventGeneratorId > 0 ? this.userService.getUserDetailsById(action.event.event.eventGeneratorId) : of(null),
        action.event.event?.eventLocationId ? this.accessPointService.getAccesspointById(action.event.event.eventLocationId) : of(null)).
        pipe(take(1)).subscribe(data => {
          try {
            let generator: User | null = null;
            let location: AccessPoint | null = null;
            if (data[0]?.user) {
              generator = data[0].user;
            }
            if (data[1]?.accessPoint) {
              location = data[1].accessPoint;
            }
            ctx.patchState({selectedEvent: {eventData: action.event, generator, location}});
          } catch (e) {
            this.monitoringService.logException(e);
            // this.store.dispatch(new AddToastMessage({
            //   severity: 'error',
            //   summary: 'Get Event Details Failed',
            //   detail: e
            // }));
          }
          ctx.patchState({isLoadingData: false});
        });
    } else {
      ctx.patchState({selectedEvent: null});
    }
  }
  @Action(SetIsPrinting)
  async setIsPrinting(ctx: StateContext<EventsStateModel>, action: SetIsPrinting) {
    ctx.patchState({isPrinting:  action.isPrinting});
  }
}
